import { supabase } from "../services/supabaseClient";

export type ObservabilitySourceStatus = "available" | "degraded" | "unavailable";

export type ObservabilitySourceSnapshot = {
  key: string;
  label: string;
  status: ObservabilitySourceStatus;
  total_count: number | null;
  recent_count: number | null;
  error_code: string | null;
};

export type ObservabilitySnapshot = {
  ok: true;
  snapshot_version: "4D-D2";
  role: "EXPLORE" | "GIFTSHOP";
  correlation_id: string;
  generated_at: string;
  window_minutes: number;
  baseline_state: "baseline_required";
  objective_evaluation: "not_activated";
  summary: { available: number; degraded: number; unavailable: number };
  sources: ObservabilitySourceSnapshot[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function normalizeWindow(windowMinutes: number) {
  if (!Number.isFinite(windowMinutes)) return 60;
  return Math.min(1440, Math.max(5, Math.trunc(windowMinutes)));
}

export async function fetchObservabilitySnapshot(windowMinutes = 60): Promise<ObservabilitySnapshot> {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;

  const accessToken = sessionData.session?.access_token;
  if (!accessToken) throw new Error("Session administrateur absente.");

  const correlationId = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `observability-${Date.now()}`;

  const { data, error } = await supabase.functions.invoke("admin-observability-read", {
    body: { window_minutes: normalizeWindow(windowMinutes) },
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "x-forfeo-correlation-id": correlationId,
    },
  });

  if (error) throw error;
  if (!isRecord(data) || data.ok !== true || !Array.isArray(data.sources)) {
    throw new Error(String(isRecord(data) ? data.error || "Snapshot invalide." : "Snapshot invalide."));
  }

  return data as unknown as ObservabilitySnapshot;
}
